import React, { useRef, useState } from 'react';
import { uploadProfilePhoto, deleteProfilePhoto } from '../../lib/storage';

interface PhotoUploadProps {
  userId: string;
  currentPhotoUrl: string | null;
  onPhotoUpdate: (url: string) => void;
}

const PhotoUpload: React.FC<PhotoUploadProps> = ({ userId, currentPhotoUrl, onPhotoUpdate }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);

    try {
      const url = await uploadProfilePhoto(file, userId);
      if (url) {
        onPhotoUpdate(url);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload photo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemovePhoto = async () => {
    if (!currentPhotoUrl || !window.confirm('Are you sure you want to remove your profile photo?')) {
      return;
    }

    setUploading(true);
    setError(null);

    try {
      await deleteProfilePhoto(userId);
      onPhotoUpdate('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove photo');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="text-center">
      <div className="relative inline-block">
        {currentPhotoUrl ? (
          <img
            src={currentPhotoUrl}
            alt="Profile"
            className="h-24 w-24 rounded-full object-cover"
          />
        ) : (
          <div className="h-24 w-24 bg-blue-100 rounded-full flex items-center justify-center">
            <svg className="h-12 w-12 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
            </svg>
          </div>
        )}
        <div className="absolute -bottom-2 -right-2 flex gap-1">
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="bg-white rounded-full p-2 shadow-md hover:bg-gray-50 disabled:opacity-50"
            title="Upload photo"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
          {currentPhotoUrl && (
            <button
              onClick={handleRemovePhoto}
              disabled={uploading}
              className="bg-white rounded-full p-2 shadow-md hover:bg-red-50 disabled:opacity-50"
              title="Remove photo"
            >
              <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
      {error && (
        <p className="mt-2 text-sm text-red-600">{error}</p>
      )}
      {uploading && (
        <p className="mt-2 text-sm text-gray-600">Uploading...</p>
      )}
      <p className="mt-2 text-xs text-gray-500">
        Max file size: 1MB. Supported formats: JPG, PNG, GIF
      </p>
    </div>
  );
};

export default PhotoUpload;